import { API_URL } from "env";

interface OrderData {
    list_products: {
        id: number;
        product_category_id: number;
        price: number;
        quantity: number;
    }[];
    address: string;
    phone: string;
    fullname: string;
    customer_id: number;
    voucher_code?: string;

    note?: string;
    payment: 1 | 2; // 1: Cash on Delivery, 2: Bank Transfer
}

interface OrderResponse {
    order_id: string;
    status: boolean;
    msg: string | null;
}

interface OrderListResponse {
    status: boolean;
    data: {
        total: number;
        current_page: number;
        items: {
            id: number;
            code: string;
            customer_id: number;
            fullname: string;
            phone: string;
            total: number;
            status: number;
            payment_status: number;
            created_at: string;
            address: string;
            order_product: {
                id: number;
                order_id: number;
                slug_product: string;
                quantity_product: number;
                name_product: string;
                avatar_product: string;
                price_product: number;
            }[];
        }[];
    };
    msg: string;
}

interface OrderDetailResponse {
    status: boolean;
    data: {
        id: number;
        customer_id: number;
        code: string;
        total: number;
        total_discount: number | null;
        fullname: string;
        phone: string;
        address: string;
        history: [
            {
                id: number;
                order_id: number;
                created_by: number;
                status: string;
                note: string;
                created_at: string;
                updated_at: string;
            }
        ]
        order_product: {
            id: number;
            status: number | null;
            discount_price: number | null;
            order_id: number;
            quantity_product: number;
            name_product: string;
            slug_product: string;
            avatar_product: string;
            price_product: number;
        }[];
    };
    msg: string;
}

async function createOrder(orderData: OrderData) {
    try {
        const formData = new FormData();
        for (const key in orderData) {
            if (key === 'list_products') {
                formData.append(key, JSON.stringify(orderData[key as keyof OrderData]));
            } else {
                formData.append(key, String(orderData[key as keyof OrderData]));
            }
        }
        formData.append('total', orderData.list_products.reduce((sum, item) => sum + item.price * item.quantity, 0).toString());
        const response = await fetch(`${API_URL.replace(/\/$/, '')}/api/v1/fe/order/create`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        console.log('Order created successfully', data);
        return data as OrderResponse;
    } catch (error) {
        console.error('Failed to create order:', error);
        return {
            order_id: '',
            status: false,
            msg: 'Failed to create order',
        };
    }
}

async function getOrderList(
    page: number, 
    limit: number, 
    sort?: string, 
    status?: number,
    search?: string,
    startDate?: string,
    endDate?: string,
    productId?: string
) : Promise<OrderListResponse | null> {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });
        if (sort) {
            params.append('sort[]=', sort);
        }
        if (status !== undefined) {
            params.append('status', status.toString());
        }
        if (search) params.append('search', search);
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        if (productId) params.append('product_id', productId);

        const response = await fetch(`${API_URL.replace(/\/$/, '')}/api/v1/fe/orders?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data as OrderListResponse;
    } catch (error) {
        console.error('Failed to fetch order list:', error);
        return null;
    }
}

async function getOrderDetail(orderId: string): Promise<OrderDetailResponse | null> {
    try {
        const response = await fetch(`${API_URL.replace(/\/$/, '')}/api/v1/fe/order/${orderId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data as OrderDetailResponse;
    } catch (error) {
        console.error('Failed to fetch order detail:', error);
        return null;
    }
}


export {
    createOrder,
    getOrderList,
    getOrderDetail,
}